import EdgeScroll from '../components/EdgeScroll';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Title, Group, Select, Table, Badge, Button, Paper, Text, Loader, Flex, Checkbox, Tooltip, Stack, Drawer, HoverCard, Modal, Textarea, ActionIcon, Alert } from '@mantine/core';
import { Lightbulb, CheckCircle, X, Funnel, CurrencyDollar, Key, Minus, TextAa, Target, CalendarBlank, TrendUp, ShieldWarning } from '@phosphor-icons/react';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../hooks/useNotifications';
import SEO from '../components/SEO';
import AnimatedLogo from '../components/AnimatedLogo';
import ExportMenu from '../components/ExportMenu';
import api from '../utils/api';
import { currencySymbol } from '../utils/money';
import StickyHeader from '../components/StickyHeader';
import DismissReasonModal from '../components/DismissReasonModal';

const OUTCOME_COLORS = { better: 'green', worse: 'red', neutral: 'gray' };

// Plakietka efektu zastosowanej zmiany (korelacja 14 dni przed / po, nie dowód przyczyny).
function OutcomeBadge({ suggestion }) {
  const { t, i18n } = useTranslation();
  const o = suggestion.outcome;
  if (suggestion.status !== 'applied' || !o || !o.before || !o.after) return null;
  const sym = currencySymbol(suggestion.currency);
  const locale = { pl: 'pl-PL', en: 'en-GB', de: 'de-DE' }[(i18n.language || 'pl').slice(0, 2)] || 'pl-PL';
  const nf = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const num = (v, suffix = '') => (v == null ? '-' : `${nf.format(Number(v))}${suffix}`);
  const lines = o.metric === 'cpa'
    ? [t('suggestions.outcome.cpa', { before: num(o.before.cpa, ` ${sym}`), after: num(o.after.cpa, ` ${sym}`) })]
    : [
        t('suggestions.outcome.ctr', { before: num(o.before.ctr, '%'), after: num(o.after.ctr, '%') }),
        t('suggestions.outcome.cpc', { before: num(o.before.cpc, ` ${sym}`), after: num(o.after.cpc, ` ${sym}`) }),
      ];
  const verdict = OUTCOME_COLORS[o.verdict] ? o.verdict : 'neutral';
  return (
    <Tooltip
      multiline w={300} withArrow position="top-start"
      label={(
        <Stack gap={2}>
          {lines.map((l) => <Text key={l} size="xs" c="white">{l}</Text>)}
          <Text size="xs" c="gray.4">{t('suggestions.outcome.window')}</Text>
          {o.confounded && <Text size="xs" c="yellow.3">{t('suggestions.outcome.confounded')}</Text>}
        </Stack>
      )}
    >
      <Badge size="xs" color={OUTCOME_COLORS[verdict]} variant="light" mb={4}
        style={{ cursor: 'help', textTransform: 'none' }}>
        {t(`suggestions.outcome.${verdict}`)}
      </Badge>
    </Tooltip>
  );
}

// Data utworzenia sugestii, krótko (np. "20 cze").
function formatSugDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
}
// Pełna data + godzina do tooltipa.
// Zwijana historia zastosowanych zmian (60 dni) z efektem; ładowana przy pierwszym otwarciu.
function AppliedHistory() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open || rows !== null) return;
    api.get('/ads/suggestions/', { params: { status: 'applied' } })
      .then(res => setRows(Array.isArray(res.data) ? res.data : res.data.results || []))
      .catch(() => setFailed(true));
  }, [open, rows]);

  const effectCell = (s) => {
    if (s.outcome) return <OutcomeBadge suggestion={s} />;
    const age = s.applied_at ? (Date.now() - new Date(s.applied_at).getTime()) / 86400000 : 99;
    if (age < 14) return <Text size="xs" c="dimmed">{t('suggestions.history.pending')}</Text>;
    return <Text size="xs" c="dimmed">–</Text>;
  };

  return (
    <Box mt="lg">
      <Button variant="subtle" color="gray" size="xs" onClick={() => setOpen(o => !o)}>
        {t('suggestions.history.title')} {open ? '▴' : '▾'}
      </Button>
      {open && (
        <Box mt="xs">
          {failed && <Text size="xs" c="red">{t('suggestions.history.error')}</Text>}
          {!failed && rows === null && <Loader size="xs" />}
          {rows && rows.length === 0 && <Text size="xs" c="dimmed">{t('suggestions.history.empty')}</Text>}
          {rows && rows.length > 0 && (
            <Table fz="xs" verticalSpacing={4} highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('suggestions.history.date')}</Table.Th>
                  <Table.Th>{t('suggestions.history.campaign')}</Table.Th>
                  <Table.Th>{t('suggestions.history.change')}</Table.Th>
                  <Table.Th>{t('suggestions.history.type')}</Table.Th>
                  <Table.Th>{t('suggestions.history.effect')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map(s => (
                  <Table.Tr key={s.id}>
                    <Table.Td>{formatSugDate(s.applied_at)}</Table.Td>
                    <Table.Td>{s.campaign_name}</Table.Td>
                    <Table.Td>{s.title}</Table.Td>
                    <Table.Td>{s.type_display || s.type}</Table.Td>
                    <Table.Td>{effectCell(s)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Box>
      )}
    </Box>
  );
}

function formatSugDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const PRIORITY_COLORS = {
  high: 'red',
  medium: 'yellow',
  low: 'gray',
};

const PRIORITY_LABELS = {
  high: 'Wysoki',
  medium: 'Średni',
  low: 'Niski',
};

const TYPE_CONFIG = {
  bid_adjustment:  { icon: TrendUp,        color: 'blue' },
  keyword_add:     { icon: Key,            color: 'green' },
  keyword_remove:  { icon: Minus,          color: 'red' },
  budget_change:   { icon: CurrencyDollar, color: 'orange' },
  ad_copy:         { icon: TextAa,         color: 'brand' },
  targeting:       { icon: Target,         color: 'violet' },
  schedule:        { icon: CalendarBlank,  color: 'cyan' },
};

// Sugestia nowej reklamy zablokowana limitem Google (3 włączone reklamy w grupie):
// pokazujemy obecne reklamy i pozwalamy wybrać, którą zastąpić. Wcześniej sugestia
// po cichu wracała na listę, a klient klikał „Zastosuj” w kółko (RedBlond, 10.2026).
function RsaLimitPicker({ suggestion, onReplace, busy }) {
  const { t } = useTranslation();
  const block = suggestion.planned_changes?.apply_block;
  if (!block || block.violation !== 'rsa_limit' || !(block.enabled_ads || []).length) return null;
  return (
    <Alert color="yellow" variant="light" p="xs" mb={6} onClick={(e) => e.stopPropagation()}>
      <Text size="xs" fw={600} mb={4}>{t('suggestions.rsaLimit.title')}</Text>
      <Text size="xs" c="dimmed" mb={6}>{t('suggestions.rsaLimit.text')}</Text>
      <Stack gap={4}>
        {block.enabled_ads.map(ad => (
          <Group key={ad.ad_id} justify="space-between" wrap="nowrap" gap="xs">
            <Text size="xs" truncate style={{ flex: 1 }}>{(ad.headlines || []).join(' | ') || ad.ad_id}</Text>
            <Button size="compact-xs" variant="light" color="yellow" loading={busy}
              onClick={() => onReplace(suggestion, ad.ad_id)}>
              {t('suggestions.rsaLimit.replace')}
            </Button>
          </Group>
        ))}
      </Stack>
    </Alert>
  );
}

function SuggestionPreview({ preview, plannedChanges, type }) {
  const { t } = useTranslation();
  // Dokładny plan zmiany rozwiązany przy tworzeniu sugestii ma priorytet - pokazuje
  // użytkownikowi CO DOKŁADNIE zrobi Droid (np. które frazy/zainteresowania).
  if (plannedChanges && Array.isArray(plannedChanges.items) && plannedChanges.items.length > 0) {
    return (
      <Box mt={4} p="6px 9px" style={{ background: 'var(--mantine-color-gray-0)', borderRadius: 6, border: '1px solid var(--mantine-color-gray-2)' }}>
        {plannedChanges.summary && (
          <Text size="xs" fw={600} c="dark" mb={4}>{plannedChanges.summary}</Text>
        )}
        <Stack gap={2}>
          {plannedChanges.items.slice(0, 12).map((it, i) => (
            <Text key={i} size="xs" c="dimmed" lh={1.35}>
              {'• '}<Text span fw={500} c="dark">{it.label}</Text>
              {it.before && it.after ? `: ${it.before} → ${it.after}` : ''}
              {!it.before && it.after ? ` → ${it.after}` : ''}
              {it.note ? ` (${it.note})` : ''}
            </Text>
          ))}
          {plannedChanges.items.length > 12 && (
            <Text size="xs" c="dimmed">{t('suggestions.preview.more', { count: plannedChanges.items.length - 12 })}</Text>
          )}
        </Stack>
      </Box>
    );
  }
  if (!preview) return null;

  if (preview.action === 'pause' && preview.keywords?.length > 0) {
    return (
      <Text size="xs" c="dimmed" mt={2}>
        {t('suggestions.preview.paused', { keywords: preview.keywords.map(k => `"${k}"`).join(', ') })}
      </Text>
    );
  }
  if (preview.action === 'pause' && preview.note) {
    return <Text size="xs" c="orange.6" mt={2}>{preview.note}</Text>;
  }
  if (preview.action === 'change' && preview.old && preview.new) {
    return (
      <Text size="xs" c="dimmed" mt={2}>
        {preview.group ? `${preview.group}: ` : ''}{preview.field}: {preview.old} → {preview.new}
      </Text>
    );
  }
  if (preview.action === 'generate') {
    return (
      <Text size="xs" c="dimmed" mt={2}>
        {preview.group ? t('suggestions.preview.group', { group: preview.group }) : ''}{preview.current_headlines ? ` ${t('suggestions.preview.currentHeadlines', { count: preview.current_headlines })}` : ''} {preview.note || ''}
      </Text>
    );
  }
  if (preview.action === 'add') {
    return (
      <Text size="xs" c="dimmed" mt={2}>
        {t('suggestions.preview.currentKeywords', { count: preview.current_count })} {preview.note || ''}
      </Text>
    );
  }
  return null;
}

const rowActionStyles = `
  .suggestion-actions { opacity: 0; transition: opacity 0.15s; }
  tr:hover .suggestion-actions { opacity: 1; }
`;

export default function Suggestions() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCampaign, setFilterCampaign] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [applyingIds, setApplyingIds] = useState([]);
  const [dismissingIds, setDismissingIds] = useState([]);
  const [dismissModal, setDismissModal] = useState(null);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkDismissing, setBulkDismissing] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation();
  const { addNotification, updateNotification } = useNotifications();
  const isAgency = user?.organization?.is_agency;
  const orgHasScheduler = user?.organization?.scheduled_actions_enabled;

  const getSuggestionState = (s) => {
    if (s.type === 'schedule' && !s.campaign_scheduler_enabled && !orgHasScheduler) return 'needs_module';
    // Sugestie informacyjne (np. niedziałający tag konwersji) nie mają czego stosować.
    if (s.planned_changes?._advisory || s.type === 'conversion_tracking') return 'manual';
    if (s.type === 'targeting') {
      const t = s.title.toLowerCase();
      if (t.includes('wykluczając') || t.includes('negatywn') || t.includes('wykluczenie')) return 'actionable';
      return 'manual';
    }
    return 'actionable';
  };

  const refetchSuggestions = React.useCallback(() => {
    return api.get('/ads/suggestions/')
      .then(res => {
        const data = res.data;
        const list = Array.isArray(data) ? data : data.results || [];
        setSuggestions(list);
        return list;
      })
      .catch(() => null); // zostaw obecną listę
  }, []);

  useEffect(() => {
    refetchSuggestions().finally(() => setLoading(false));
  }, [refetchSuggestions]);

  const [contextModal, setContextModal] = useState(null); // suggestion object
  const [extraContext, setExtraContext] = useState('');
  const [safetyModal, setSafetyModal] = useState(null); // { suggestion, context, message }

  const handleApplyClick = (suggestion) => {
    if (suggestion.type === 'ad_copy') {
      setContextModal(suggestion);
      setExtraContext('');
    } else {
      handleApply(suggestion);
    }
  };

  const handleApplyWithContext = () => {
    if (!contextModal) return;
    handleApply(contextModal, extraContext);
    setContextModal(null);
    setExtraContext('');
  };

  const handleApply = async (suggestion, context = '', force = false, replaceAdId = null) => {
    const sid = suggestion.id;
    const clearApplying = () => setApplyingIds(prev => prev.filter(id => id !== sid));
    setApplyingIds(prev => [...prev, sid]);
    const notifId = addNotification(t('suggestions.notif.applying', { title: suggestion.title }), 'loading');
    try {
      const payload = {};
      if (context) payload.extra_context = context;
      if (force) payload.force = true;
      if (replaceAdId) payload.replace_ad_id = replaceAdId;
      const res = await api.post(`/ads/suggestions/${sid}/apply/`, payload);

      if (res.data.status === 'safety_warning') {
        clearApplying();
        updateNotification(notifId, { message: '', type: 'dismiss' });
        setSafetyModal({ suggestion, context, message: res.data.message });
        return;
      }
      if (res.data.status === 'applied' || res.data.status === 'dismissed') {
        // Serwer już rozwiązał tę sugestię (idempotentny ponowny klik).
        setSuggestions(prev => prev.filter(s => s.id !== sid));
        setSelectedIds(prev => { const next = new Set(prev); next.delete(sid); return next; });
        updateNotification(notifId, { message: t('suggestions.notif.applied', { title: suggestion.title }), type: 'success' });
        clearApplying();
        return;
      }

      // status === 'applying': zmiana leci w tle (zadanie Celery). Zdejmujemy sugestię
      // OD RAZU - płynnie, bez kręciołka. Backend UKRYWA stosowane sugestie z listy
      // (blokada), więc odświeżenie w trakcie NIE przywróci jej. Wróci tylko, gdy
      // stosowanie zawiedzie (nadal pending, blokada zwolniona) - łapiemy to dwoma
      // opóźnionymi odświeżeniami (po typowym i dłuższym czasie zadania).
      setSuggestions(prev => prev.filter(s => s.id !== sid));
      setSelectedIds(prev => { const next = new Set(prev); next.delete(sid); return next; });
      updateNotification(notifId, { message: t('suggestions.notif.applied', { title: suggestion.title }), type: 'success' });
      clearApplying();
      setTimeout(() => { refetchSuggestions(); }, 12000);
      setTimeout(() => { refetchSuggestions(); }, 30000);
    } catch {
      updateNotification(notifId, { message: t('suggestions.notif.applyError', { title: suggestion.title }), type: 'error' });
      clearApplying();
    }
  };

  const handleDismiss = (suggestion) => setDismissModal(suggestion);

  const confirmDismiss = async ({ reason, remember }) => {
    const suggestion = dismissModal;
    if (!suggestion) return;
    setDismissModal(null);
    setDismissingIds(prev => [...prev, suggestion.id]);
    const notifId = addNotification(t('suggestions.notif.dismissing', { title: suggestion.title }), 'loading');
    try {
      await api.post(`/ads/suggestions/${suggestion.id}/dismiss/`, { reason, remember });
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
      setSelectedIds(prev => { const next = new Set(prev); next.delete(suggestion.id); return next; });
      updateNotification(notifId, { message: t('suggestions.notif.dismissed', { title: suggestion.title }), type: 'info' });
    } catch {
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
      setSelectedIds(prev => { const next = new Set(prev); next.delete(suggestion.id); return next; });
      updateNotification(notifId, { message: t('suggestions.notif.dismissed', { title: suggestion.title }), type: 'info' });
    } finally {
      setDismissingIds(prev => prev.filter(id => id !== suggestion.id));
    }
  };

  const handleBulkApply = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setBulkApplying(true);
    const notifId = addNotification(t('suggestions.notif.applyingBulk', { count: ids.length }), 'loading');
    let applied = 0;
    for (const id of ids) {
      try {
        await api.post(`/ads/suggestions/${id}/apply/`);
        applied++;
      } catch { /* continue */ }
    }
    setSuggestions(prev => prev.filter(s => !selectedIds.has(s.id)));
    setSelectedIds(new Set());
    setBulkApplying(false);
    updateNotification(notifId, { message: t('suggestions.notif.appliedBulk', { applied, total: ids.length }), type: 'success' });
  };

  const handleBulkDismiss = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setBulkDismissing(true);
    const notifId = addNotification(t('suggestions.notif.dismissingBulk', { count: ids.length }), 'loading');
    let dismissed = 0;
    for (const id of ids) {
      try {
        await api.post(`/ads/suggestions/${id}/dismiss/`);
        dismissed++;
      } catch { /* continue */ }
    }
    setSuggestions(prev => prev.filter(s => !selectedIds.has(s.id)));
    setSelectedIds(new Set());
    setBulkDismissing(false);
    updateNotification(notifId, { message: t('suggestions.notif.dismissedBulk', { dismissed, total: ids.length }), type: 'info' });
  };

  // Build filter options from data
  const campaignNames = [...new Set(suggestions.map(s => s.campaign_name).filter(Boolean))].sort();
  const typeKeys = [...new Set(suggestions.map(s => s.type).filter(Boolean))].sort();
  const clientNames = [...new Set(suggestions.map(s => s.agency_client_name).filter(Boolean))].sort();

  const filtered = suggestions.filter(s => {
    if (filterPriority !== 'all' && s.priority !== filterPriority) return false;
    if (filterCampaign !== 'all' && s.campaign_name !== filterCampaign) return false;
    if (filterType !== 'all' && s.type !== filterType) return false;
    if (filterClient !== 'all' && (s.agency_client_name || '') !== filterClient) return false;
    return true;
  });

  const allFilteredSelected = filtered.length > 0 && filtered.every(s => selectedIds.has(s.id));
  const someSelected = selectedIds.size > 0;

  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(s => s.id)));
    }
  };

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const hasActiveFilters = filterPriority !== 'all' || filterCampaign !== 'all' || filterType !== 'all' || filterClient !== 'all';
  const clearAllFilters = () => {
    setFilterPriority('all'); setFilterCampaign('all'); setFilterType('all'); setFilterClient('all');
    setSelectedIds(new Set()); setShowFilterDrawer(false);
  };

  if (loading) return (
    <Flex align="center" justify="center" h={256}>
      <AnimatedLogo size={48} />
    </Flex>
  );

  return (
    <Box px="lg" py="md">
      <SEO title={t('suggestions.seoTitle')} path="/app/suggestions" noIndex />
      <style>{rowActionStyles}</style>
      {/* Header */}
      <StickyHeader>
        <Title order={3}>{t('suggestions.pageTitle')}</Title>
        <ExportMenu
          baseUrl="/ads/suggestions/export/"
          filename="droid-sugestie"
          filters={[
            filterCampaign !== 'all' ? `campaign_name=${encodeURIComponent(filterCampaign)}` : '',
            filterType !== 'all' ? `type=${filterType}` : '',
            filterPriority !== 'all' ? `priority=${filterPriority}` : '',
          ].filter(Boolean).join('&')}
        />
      </StickyHeader>
      {/* Desktop filters */}
      <Group gap="xs" mb="md" className="hidden md:flex">
        {campaignNames.length > 0 && (
          <Select placeholder={t('suggestions.filters.campaign')} value={filterCampaign === 'all' ? null : filterCampaign} onChange={(v) => { setFilterCampaign(v || 'all'); setSelectedIds(new Set()); }} data={campaignNames.map(n => ({ value: n, label: n }))} size="sm" style={{ width: 200 }} clearable />
        )}
        {typeKeys.length > 1 && (
          <Select placeholder={t('suggestions.filters.type')} value={filterType === 'all' ? null : filterType} onChange={(v) => { setFilterType(v || 'all'); setSelectedIds(new Set()); }} data={typeKeys.map(tk => ({ value: tk, label: t(`suggestions.typeFilters.${tk}`, { defaultValue: tk }) }))} size="sm" style={{ width: 200 }} clearable />
        )}
        <Select placeholder={t('suggestions.filters.priority')} value={filterPriority === 'all' ? null : filterPriority} onChange={(v) => { setFilterPriority(v || 'all'); setSelectedIds(new Set()); }} data={[{ value: 'high', label: t('suggestions.priorities.high') },{ value: 'medium', label: t('suggestions.priorities.medium') },{ value: 'low', label: t('suggestions.priorities.low') }]} size="sm" style={{ width: 160 }} clearable />
        {isAgency && clientNames.length > 0 && (
          <Select placeholder={t('suggestions.filters.client')} value={filterClient === 'all' ? null : filterClient} onChange={(v) => { setFilterClient(v || 'all'); setSelectedIds(new Set()); }} data={clientNames.map(n => ({ value: n, label: n }))} size="sm" style={{ width: 180 }} clearable />
        )}
      </Group>
      {/* Mobile filter button */}
      <div className="md:hidden mb-3">
        <Button variant="default" size="sm" leftSection={<Funnel size={14} />} onClick={() => setShowFilterDrawer(true)}>
          {t('suggestions.filters.button')}{hasActiveFilters ? ` ${t('suggestions.filters.activeSuffix')}` : ''}
        </Button>
      </div>
      <DismissReasonModal
        suggestion={dismissModal}
        onClose={() => setDismissModal(null)}
        onConfirm={confirmDismiss}
      />
      <Drawer opened={showFilterDrawer} onClose={() => setShowFilterDrawer(false)} title={t('suggestions.filters.drawerTitle')} position="bottom" size="auto" padding="md" className="md:hidden">
        <Stack gap="sm">
          {campaignNames.length > 0 && (
            <Select label={t('suggestions.filters.campaign')} placeholder={t('suggestions.filters.all')} value={filterCampaign === 'all' ? null : filterCampaign} onChange={(v) => { setFilterCampaign(v || 'all'); setSelectedIds(new Set()); }} data={campaignNames.map(n => ({ value: n, label: n }))} size="sm" clearable />
          )}
          {typeKeys.length > 1 && (
            <Select label={t('suggestions.filters.type')} placeholder={t('suggestions.filters.all')} value={filterType === 'all' ? null : filterType} onChange={(v) => { setFilterType(v || 'all'); setSelectedIds(new Set()); }} data={typeKeys.map(tk => ({ value: tk, label: t(`suggestions.typeFilters.${tk}`, { defaultValue: tk }) }))} size="sm" clearable />
          )}
          <Select label={t('suggestions.filters.priority')} placeholder={t('suggestions.filters.all')} value={filterPriority === 'all' ? null : filterPriority} onChange={(v) => { setFilterPriority(v || 'all'); setSelectedIds(new Set()); }} data={[{ value: 'high', label: t('suggestions.priorities.high') },{ value: 'medium', label: t('suggestions.priorities.medium') },{ value: 'low', label: t('suggestions.priorities.low') }]} size="sm" clearable />
          {isAgency && clientNames.length > 0 && (
            <Select label={t('suggestions.filters.client')} placeholder={t('suggestions.filters.allClients')} value={filterClient === 'all' ? null : filterClient} onChange={(v) => { setFilterClient(v || 'all'); setSelectedIds(new Set()); }} data={clientNames.map(n => ({ value: n, label: n }))} size="sm" clearable />
          )}
          <Group gap="xs" mt="xs">
            <Button size="sm" color="brand" onClick={() => setShowFilterDrawer(false)}>{t('suggestions.filters.apply')}</Button>
            {hasActiveFilters && <Button size="sm" variant="subtle" color="gray" onClick={clearAllFilters}>{t('suggestions.filters.clear')}</Button>}
          </Group>
        </Stack>
      </Drawer>

      {/* Bulk actions bar */}
      {someSelected && (
        <Paper withBorder p="xs" px="sm" radius="md" mb="sm" bg="brand.0">
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              {t('suggestions.bulk.selected', { count: selectedIds.size })}
            </Text>
            <Group gap="xs">
              <Button
                size="compact-sm"
                variant="light"
                color="brand"
                leftSection={<CheckCircle size={14} />}
                loading={bulkApplying}
                onClick={handleBulkApply}
              >
                {t('suggestions.bulk.applySelected')}
              </Button>
              <Button
                size="compact-sm"
                variant="light"
                color="gray"
                leftSection={<X size={14} />}
                loading={bulkDismissing}
                onClick={handleBulkDismiss}
              >
                {t('suggestions.bulk.dismissSelected')}
              </Button>
            </Group>
          </Group>
        </Paper>
      )}

      {/* Content */}
      {filtered.length > 0 ? (
        <>
        {/* Mobile cards */}
        <div className="md:hidden">
          <Stack gap="xs">
            {filtered.map(suggestion => {
              const state = getSuggestionState(suggestion);
              return (
              <Paper
                key={suggestion.id}
                withBorder
                p="sm"
                radius="md"
                onClick={() => setExpandedId(expandedId === suggestion.id ? null : suggestion.id)}
                style={{
                  cursor: 'pointer',
                  borderLeft: `3px solid ${suggestion.priority === 'high' ? 'var(--mantine-color-red-5)' : suggestion.priority === 'medium' ? 'var(--mantine-color-yellow-5)' : 'var(--mantine-color-gray-3)'}`,
                  opacity: state === 'needs_module' ? 0.6 : 1,
                }}
              >
                <Group justify="space-between" mb={4}>
                  {(() => {
                    const tc = TYPE_CONFIG[suggestion.type] || { icon: Lightbulb, color: 'gray' };
                    const TypeIcon = tc.icon;
                    const typeLabel = t(`suggestions.types.${suggestion.type}`, { defaultValue: suggestion.type });
                    return (
                      <Group gap={5}>
                        <TypeIcon size={14} weight="bold" style={{ color: `var(--mantine-color-${tc.color}-5)` }} />
                        <Text size="xs" fw={500} c={`${tc.color}.7`}>{typeLabel}</Text>
                      </Group>
                    );
                  })()}
                  <Group gap={6} wrap="nowrap">
                    <Text size="xs" c="dimmed">{suggestion.campaign_name}</Text>
                    {suggestion.created_at && (
                      <Text size="xs" c="dimmed" title={formatSugDateTime(suggestion.created_at)}>
                        · {formatSugDate(suggestion.created_at)}
                      </Text>
                    )}
                  </Group>
                </Group>
                <Text size="sm" fw={500} mb={2}>{suggestion.title}</Text>
                {suggestion.safety_warning && (
                  <Tooltip label={suggestion.safety_warning} multiline w={280} withArrow position="top-start">
                    <Badge size="xs" color="yellow" variant="light" mb={4}
                      leftSection={<ShieldWarning size={11} weight="bold" />}
                      style={{ cursor: 'help', textTransform: 'none' }}>
                      {t('suggestions.safetyBadge')}
                    </Badge>
                  </Tooltip>
                )}
                <OutcomeBadge suggestion={suggestion} />
                <SuggestionPreview preview={suggestion.preview} plannedChanges={suggestion.planned_changes} type={suggestion.type} />
                <RsaLimitPicker suggestion={suggestion} busy={applyingIds.includes(suggestion.id)} onReplace={(sg, adId) => handleApply(sg, '', false, adId)} />
                {suggestion.expected_impact && (
                  <Text size="xs" c="dark" fw={500} mb={6}>{suggestion.expected_impact}</Text>
                )}
                {expandedId === suggestion.id && suggestion.ai_reasoning && (
                  <Paper bg="gray.0" p="xs" radius="sm" withBorder mb={6}>
                    <Text size="xs" c="dark.4">{suggestion.ai_reasoning || suggestion.description}</Text>
                  </Paper>
                )}
                {state === 'needs_module' || state === 'manual' ? (
                  <Group justify="space-between" align="center" gap="xs" onClick={(e) => e.stopPropagation()}>
                    <Text size="xs" c="dimmed" fs="italic" style={{ flex: 1 }}>
                      {state === 'needs_module'
                        ? t('suggestions.needsModuleText')
                        : t('suggestions.manualText')}
                    </Text>
                    <Button variant="subtle" color="gray" size="compact-sm" leftSection={<X size={14} />}
                      onClick={() => handleDismiss(suggestion)}
                      loading={dismissingIds.includes(suggestion.id)}
                      style={{ flexShrink: 0 }}
                    >{t('suggestions.actions.dismiss')}</Button>
                  </Group>
                ) : (
                <Group gap="xs" onClick={(e) => e.stopPropagation()}>
                  <Button variant="outline" color="brand" size="compact-sm" leftSection={<CheckCircle size={14} />}
                    onClick={() => handleApplyClick(suggestion)}
                    loading={applyingIds.includes(suggestion.id)}
                  >{t('suggestions.actions.apply')}</Button>
                  <Button variant="outline" color="gray" size="compact-sm" leftSection={<X size={14} />}
                    onClick={() => handleDismiss(suggestion)}
                    loading={dismissingIds.includes(suggestion.id)}
                  >{t('suggestions.actions.dismiss')}</Button>
                </Group>
                )}
              </Paper>
              );
            })}
          </Stack>
        </div>
        {/* Desktop table */}
        <Paper withBorder radius="md" style={{ overflow: 'hidden' }} className="hidden md:block">
          <EdgeScroll>
          <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={36}>
                  <Checkbox
                    checked={allFilteredSelected}
                    indeterminate={someSelected && !allFilteredSelected}
                    onChange={toggleAll}
                    size="xs"
                    styles={{ input: { cursor: 'pointer' } }}
                  />
                </Table.Th>
                <Table.Th w={36}></Table.Th>
                <Table.Th>{t('suggestions.table.type')}</Table.Th>
                <Table.Th>{t('suggestions.table.campaign')}</Table.Th>
                <Table.Th>{t('suggestions.table.date')}</Table.Th>
                <Table.Th>{t('suggestions.table.title')}</Table.Th>
                <Table.Th>{t('suggestions.table.impact')}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>{t('suggestions.table.actions')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.map(suggestion => {
                const state = getSuggestionState(suggestion);
                return (
                <React.Fragment key={suggestion.id}>
                  <HoverCard width={400} position="bottom-start" withArrow shadow="md" openDelay={400} closeDelay={100}>
                    <HoverCard.Target>
                      <Table.Tr style={state !== 'actionable' ? { opacity: 0.55 } : undefined}>
                        <Table.Td onClick={(e) => e.stopPropagation()}>
                          {state === 'actionable' ? (
                            <Checkbox
                              checked={selectedIds.has(suggestion.id)}
                              onChange={() => toggleOne(suggestion.id)}
                              size="xs"
                              styles={{ input: { cursor: 'pointer' } }}
                            />
                          ) : null}
                        </Table.Td>
                        <Table.Td>
                          <Box
                            w={10}
                            h={10}
                            style={{
                              borderRadius: '50%',
                              backgroundColor: suggestion.priority === 'high' ? 'var(--mantine-color-red-5)' :
                                suggestion.priority === 'medium' ? 'var(--mantine-color-yellow-5)' : 'var(--mantine-color-gray-4)',
                            }}
                          />
                        </Table.Td>
                        <Table.Td>
                          {(() => {
                            const tc = TYPE_CONFIG[suggestion.type] || { icon: Lightbulb, color: 'gray' };
                            const TypeIcon = tc.icon;
                            const typeLabel = t(`suggestions.types.${suggestion.type}`, { defaultValue: suggestion.type });
                            return (
                              <Group gap={6} wrap="nowrap">
                                <TypeIcon size={15} weight="bold" style={{ color: `var(--mantine-color-${tc.color}-5)`, flexShrink: 0 }} />
                                <Text size="xs" fw={500} c={`${tc.color}.7`}>{typeLabel}</Text>
                              </Group>
                            );
                          })()}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed" truncate maw={180}>{suggestion.campaign_name}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }} title={formatSugDateTime(suggestion.created_at)}>
                            {formatSugDate(suggestion.created_at)}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={500}>{suggestion.title}</Text>
                          <SuggestionPreview preview={suggestion.preview} plannedChanges={suggestion.planned_changes} type={suggestion.type} />
                          <RsaLimitPicker suggestion={suggestion} busy={applyingIds.includes(suggestion.id)} onReplace={(sg, adId) => handleApply(sg, '', false, adId)} />
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dark">{suggestion.expected_impact}</Text>
                        </Table.Td>
                        <Table.Td>
                          {state === 'needs_module' || state === 'manual' ? (
                            <Group gap={6} justify="flex-end" wrap="nowrap" align="center">
                              <Text size="xs" c="dimmed" fs="italic" ta="right">
                                {state === 'needs_module' ? t('suggestions.needsModuleShort') : t('suggestions.manualShort')}
                              </Text>
                              <Tooltip label={t('suggestions.actions.dismiss')} position="top">
                                <ActionIcon
                                  variant="light"
                                  color="gray"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); handleDismiss(suggestion); }}
                                  disabled={dismissingIds.includes(suggestion.id)}
                                  loading={dismissingIds.includes(suggestion.id)}
                                >
                                  <X size={16} weight="bold" />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          ) : (
                          <Group gap={2} justify="flex-end" wrap="nowrap">
                            <Tooltip label={suggestion.type === 'schedule' ? t('suggestions.actions.schedule') : t('suggestions.actions.apply')} position="top">
                              <ActionIcon
                                variant="filled"
                                color="brand"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleApplyClick(suggestion); }}
                                disabled={applyingIds.includes(suggestion.id)}
                                loading={applyingIds.includes(suggestion.id)}
                                style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                              >
                                <CheckCircle size={14} weight="bold" />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label={t('suggestions.actions.dismiss')} position="top">
                              <ActionIcon
                                variant="light"
                                color="brand"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleDismiss(suggestion); }}
                                disabled={dismissingIds.includes(suggestion.id)}
                                loading={dismissingIds.includes(suggestion.id)}
                                style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                              >
                                <X size={16} weight="bold" />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    </HoverCard.Target>
                    {(suggestion.ai_reasoning || suggestion.description) && (
                      <HoverCard.Dropdown style={{ border: '1px solid var(--mantine-color-gray-2)' }}>
                        <Group gap="xs" mb={6}>
                          <Lightbulb size={14} weight="fill" color="var(--mantine-color-brand-5)" />
                          <Text size="xs" fw={600} c="brand">{t('suggestions.aiReasoning')}</Text>
                        </Group>
                        <Text size="sm" c="dark.5" lh={1.6}>{suggestion.ai_reasoning || suggestion.description}</Text>
                        {suggestion.expected_impact && (
                          <Text size="xs" c="dark" fw={500} mt={8}>{t('suggestions.estimatedImpact', { impact: suggestion.expected_impact })}</Text>
                        )}
                      </HoverCard.Dropdown>
                    )}
                  </HoverCard>
                </React.Fragment>
                );
              })}
            </Table.Tbody>
          </Table>
          </EdgeScroll>
        </Paper>
        </>
      ) : (
        <Paper withBorder radius="md" p="xl" style={{ textAlign: 'center' }}>
          <Lightbulb size={32} weight="light" color="var(--mantine-color-gray-4)" />
          <Text size="sm" c="dimmed" mt="sm">
            {suggestions.length > 0 ? t('suggestions.emptyFiltered') : t('suggestions.empty')}
          </Text>
        </Paper>
      )}

      <Modal
        opened={!!contextModal}
        onClose={() => setContextModal(null)}
        title={<Text size="sm" fw={600}>{t('suggestions.contextModal.title')}</Text>}
        centered
        size="md"
      >
        {contextModal && (
          <Stack gap="sm">
            <Paper bg="gray.0" p="sm" radius="sm">
              <Text size="xs" c="dimmed" mb={2}>{t('suggestions.suggestionLabel')}</Text>
              <Text size="sm" fw={500}>{contextModal.title}</Text>
              <Text size="xs" c="dimmed" mt={4}>{contextModal.description}</Text>
            </Paper>
            <Textarea
              label={t('suggestions.contextModal.contextLabel')}
              placeholder={t('suggestions.contextModal.contextPlaceholder')}
              value={extraContext}
              onChange={e => setExtraContext(e.target.value)}
              rows={3}
              size="sm"
              description={t('suggestions.contextModal.contextDescription')}
            />
            <Group justify="flex-end" gap="xs">
              <Button variant="outline" color="gray" size="sm" onClick={() => setContextModal(null)}>
                {t('suggestions.actions.cancel')}
              </Button>
              <Button color="brand" size="sm" onClick={handleApplyWithContext} loading={applyingIds.includes(contextModal.id)}>
                {extraContext.trim() ? t('suggestions.contextModal.generateWithContext') : t('suggestions.contextModal.generateWithoutContext')}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Safety warning modal */}
      <Modal
        opened={!!safetyModal}
        onClose={() => setSafetyModal(null)}
        title={t('suggestions.safetyModal.title')}
        centered
      >
        {safetyModal && (
          <Stack gap="md">
            <Alert color="yellow" icon={<Lightbulb size={16} />}>
              <Text size="sm">{safetyModal.message}</Text>
            </Alert>
            <Paper bg="gray.0" p="sm" radius="sm">
              <Text size="xs" c="dimmed" mb={2}>{t('suggestions.suggestionLabel')}</Text>
              <Text size="sm" fw={500}>{safetyModal.suggestion.title}</Text>
            </Paper>
            <Text size="xs" c="dimmed">
              {t('suggestions.safetyModal.body')}
            </Text>
            <Group justify="flex-end" gap="xs">
              <Button variant="default" size="sm" onClick={() => setSafetyModal(null)}>
                {t('suggestions.actions.cancel')}
              </Button>
              <Button color="brand" size="sm" onClick={() => {
                const { suggestion, context } = safetyModal;
                setSafetyModal(null);
                handleApply(suggestion, context, true);
              }}>
                {t('suggestions.safetyModal.applyAnyway')}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
      <AppliedHistory />
    </Box>
  );
}
